import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Snackbar,
    useTheme,
    Container,
    Fab,
    Alert,
    Slide, TextField, FormControlLabel, Checkbox, Radio, RadioGroup, FormControl, FormLabel, MenuItem, Select, Link
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Zoom from '@mui/material/Zoom';
import { Link as RouterLink } from 'react-router-dom';
import { MuiTelInput } from 'mui-tel-input';
import useParallaxEffect from "../common/useParallaxEffect";
import ScrollToTop from "../common/ScrollToTop";
import { apiService } from '../../services/api';

const SlideTransition = forwardRef(function Transition(props, ref) {
    const { children, ...other } = props;
    return (
        <Slide direction="down" ref={ref} {...other}>
            {children}
        </Slide>
    );
});

const Contact = () => {
    useParallaxEffect();
    const theme = useTheme();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        smsConsent: false,
        preferredContact: 'email',
        serviceInterest: '',
        isNewClient: 'true',
        message: '',
        agreeToTerms: false
    });

    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const validateForm = useCallback(() => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        else if (!/^\+1 \d{3} \d{3} \d{4}$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid US phone number';
        }
        if (!formData.message.trim()) newErrors.message = 'Message is required';
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
        return newErrors;
    }, [formData]);

    const checkFormValidity = useCallback(() => {
        const formErrors = validateForm();
        return Object.keys(formErrors).length === 0;
    }, [validateForm]);

    useEffect(() => {
        window.scrollTo(0, 0);
        checkFormValidity();
    }, [checkFormValidity]);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handlePhoneChange = (value) => {
        // Remove any non-digit characters
        const digitsOnly = value.replace(/\D/g, '');

        // Format the phone number
        let formattedNumber = '';
        if (digitsOnly.length > 0) {
            formattedNumber = `+1 ${digitsOnly.slice(1, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 11)}`;
        }

        setFormData(prev => ({ ...prev, phone: formattedNumber }));
        if (errors.phone) {
            setErrors(prev => ({ ...prev, phone: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        setErrors(formErrors);
        if (Object.keys(formErrors).length > 0) {
            const firstErrorField = document.querySelector('.Mui-error');
            if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        try {
            setSubmitted(true);
            await apiService.submitContactUsForm(formData);
            setSnackbarOpen(true);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                smsConsent: false,
                preferredContact: 'email',
                serviceInterest: '',
                isNewClient: 'true',
                message: '',
                agreeToTerms: false
            });
            setErrors({});
        } catch (error) {
            console.error('Error submitting form:', error);
            setSnackbarOpen(true);
            setErrors({ submit: 'Failed to submit form. Please try again.' });
        } finally {
            setSubmitted(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <>
            <div id="back-to-top-anchor"/>
            <Box>
                <Box sx={{
                    bgcolor: '#f5f5f5',
                    pt: '64px',
                    pb: '30px',
                }}>
                    <Container maxWidth="lg">
                        <Typography variant="h5" component="h3" align="center" sx={{ color: '#333', fontWeight: 500 }}>
                            We're here to help. Reach out to us with any questions or to schedule an appointment.
                        </Typography>
                    </Container>
                </Box>

                <Container maxWidth="lg">
                    <Box sx={{ width: '100%', maxWidth: '90%', mx: 'auto', mb: 6 }}>
                        <Paper sx={{p: 4, borderRadius: 2, boxShadow: 3}}>
                            <Typography variant="h4" component="h2" gutterBottom sx={{
                                fontSize: '1.8rem',
                                fontWeight: 'bold',
                                color: theme.palette.secondary.main,
                                mb: 3
                            }}>
                                Contact Form
                            </Typography>
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                    margin="normal"
                                />
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                    margin="normal"
                                />
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    margin="normal"
                                />
                                <MuiTelInput
                                    fullWidth
                                    label="Phone"
                                    value={formData.phone}
                                    onChange={handlePhoneChange}
                                    error={!!errors.phone}
                                    helperText={errors.phone}
                                    margin="normal"
                                    defaultCountry="US"
                                    onlyCountries={['US']}
                                    forceCallingCode
                                    disableDropdown
                                />
                                <FormControlLabel
                                    sx={{ alignItems: 'flex-start', mt: 1, mb: 1 }}
                                    control={
                                        <Checkbox
                                            checked={formData.smsConsent}
                                            onChange={handleChange}
                                            name="smsConsent"
                                            sx={{ pt: 0 }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" component="span">
                                            I consent to receive appointment and scheduling text messages from Pivotal Behavioral Services. Message frequency varies (approximately 1–4 messages per month). Msg & data rates may apply. Reply STOP to unsubscribe, HELP for help. View our T&D and Privacy Policy{' '}
                                            <Link component={RouterLink} to="/privacy" underline="hover">
                                                here
                                            </Link>
                                            .
                                        </Typography>
                                    }
                                />
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Preferred Contact Method</FormLabel>
                                    <RadioGroup
                                        name="preferredContact"
                                        value={formData.preferredContact}
                                        onChange={handleChange}
                                    >
                                        <FormControlLabel value="email" control={<Radio />} label="Email" />
                                        <FormControlLabel value="phone" control={<Radio />} label="Phone" />
                                    </RadioGroup>
                                </FormControl>
                                <FormControl fullWidth margin="normal">
                                    <FormLabel>Service Interest (Optional)</FormLabel>
                                    <Select
                                        name="serviceInterest"
                                        value={formData.serviceInterest}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            <em>Select a service</em>
                                        </MenuItem>
                                        <MenuItem value="in-home ABA therapy">In-home ABA therapy</MenuItem>
                                        <MenuItem value="In-center ABA Therapy">In-center ABA Therapy</MenuItem>
                                        <MenuItem value="In-school ABA support">In-school ABA support</MenuItem>
                                        <MenuItem value="Virtual ABA Therapy">Virtual ABA Therapy</MenuItem>
                                        <MenuItem value="Parent Engagement Model">Parent Engagement Model</MenuItem>
                                        <MenuItem value="Mentorship">Mentorship</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl component="fieldset" margin="normal">
                                    <FormLabel component="legend">Are you a new client?</FormLabel>
                                    <RadioGroup
                                        name="isNewClient"
                                        value={formData.isNewClient}
                                        onChange={handleChange}
                                    >
                                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="false" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    label="Message"
                                    name="message"
                                    multiline
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleChange}
                                    error={!!errors.message}
                                    helperText={errors.message}
                                    margin="normal"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.agreeToTerms}
                                            onChange={handleChange}
                                            name="agreeToTerms"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" component="span">
                                            I agree to the{' '}
                                            <Link component={RouterLink} to="/privacy" underline="hover">
                                                Privacy Policy
                                            </Link>
                                            {' '}and terms and conditions
                                        </Typography>
                                    }
                                />
                                {errors.agreeToTerms && (
                                    <Typography color="error">{errors.agreeToTerms}</Typography>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        sx={{
                                            bgcolor: '#C09C2C',
                                            color: 'white',
                                            fontSize: '1.2rem',
                                            py: 1.5,
                                            width: { xs: '100%', sm: 'auto', lg: '30%' },
                                            '&:hover': { bgcolor: '#C7AE6A' },
                                        }}
                                    >
                                        {submitted ? 'Submitting...' : 'Submit'}
                                    </Button>
                                </Box>
                            </form>
                        </Paper>
                    </Box>
                </Container>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    TransitionComponent={SlideTransition}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={errors.submit ? "error" : "success"}
                        sx={{ width: '100%' }}
                        variant="filled"
                        elevation={6}
                    >
                        {errors.submit || "Message sent successfully!"}
                    </Alert>
                </Snackbar>
                <Zoom>
                    <Box
                        role="presentation"
                        sx={{
                            position: 'fixed',
                            bottom: 32,
                            right: 32,
                            zIndex: 1000,
                        }}
                    >
                        <Fab color="primary" size="small" aria-label="scroll back to top">
                            <KeyboardArrowUpIcon/>
                        </Fab>
                    </Box>
                </Zoom>
            </Box>
            <ScrollToTop />
        </>
    );
};

export default Contact;